import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { since } from "@/lib/lastSeen";
import FilterableTable from "@/components/FilterableTable";

const money = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN");

export default function ConsultantsPage() {
  const [rows, setRows] = useState([]);
  const [statements, setStatements] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [issued, setIssued] = useState(null);
  // Keep the previewed workbook so Save can re-post the same file. Without
  // this, confirming reopens the OS file picker, which reads as a failure
  // and leaves the month previewed but never saved.
  const [pending, setPending] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mailOk, setMailOk] = useState(false);
  const [mailing, setMailing] = useState("");
  const [picked, setPicked] = useState([]);
  const [notifying, setNotifying] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/consultants");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // Is Gmail wired up? If not we hide the email buttons rather than letting an
  // admin click one and get a 500 back.
  useEffect(() => {
    api.get("/mail/status")
      .then((r) => setMailOk(!!r.data?.configured))
      .catch(() => setMailOk(false));
  }, []);

  const emailInvite = async (c) => {
    if (!window.confirm(`Email a set-password link to ${c.name} at ${c.email}?`)) return;
    setMailing(c.id);
    try {
      const r = await api.post(`/consultants/${c.id}/send-invite`);
      if (r.data?.ok) toast.success(`Invite sent to ${c.email}`);
      else toast.error(r.data?.error || "Could not send");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setMailing(""); }
  };

  const bulkInvite = async () => {
    if (!window.confirm("Email a set-password link to every active consultant who has not set one up yet?")) return;
    setMailing("bulk");
    try {
      const d = (await api.post("/consultants/send-invites", {})).data || {};
      toast.success(`Sent ${d.sent || 0}` + (d.failed?.length ? `, ${d.failed.length} failed` : ""));
      (d.failed || []).forEach((f) => toast.error(`${f.name}: ${f.error}`));
      (d.skipped || []).forEach((f) => toast.info(`Skipped ${f.name}: ${f.reason}`));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setMailing(""); }
  };

  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // Sent after the money has actually left, not when the statement is ready -
  // those are two different emails and the doctors notice the difference.
  // Shares the `notifying` flag so the two buttons cannot both fire at once.
  const notifyPaid = async () => {
    if (!picked.length) { toast.error("Select at least one statement"); return; }
    const names = statements
      .filter((x) => picked.includes(x.id))
      .map((x) => x.doctor_name || x.id);
    if (!window.confirm(
      `Send a payment confirmation to ${names.length} doctor(s)?\n\n` +
      names.join("\n") +
      `\n\nOnly do this once the money has actually gone out.`)) return;
    setNotifying(true);
    try {
      const d = (await api.post("/consultants/statements/payment-notify",
                                { statement_ids: picked })).data || {};
      toast.success(`Payment confirmation sent to ${d.sent_count || 0} doctor(s)`);
      const skipped = d.skipped || [];
      if (skipped.length) {
        toast.error(
          `Skipped ${skipped.length}: ` +
          skipped.map((x) => `${x.name} (${x.reason})`).join("; "),
          { duration: 12000 });
      }
      loadStatements(month);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNotifying(false);
    }
  };

  const notifySelected = async () => {
    if (!picked.length) { toast.error("Select at least one statement"); return; }
    if (!window.confirm(`Email ${picked.length} doctor(s) that their statement is ready?`)) return;
    setNotifying(true);
    try {
      const d = (await api.post("/consultants/statements/notify", { statement_ids: picked })).data || {};
      toast.success(`Sent ${d.sent || 0}` + (d.failed?.length ? `, ${d.failed.length} failed` : ""));
      (d.failed || []).forEach((f) => toast.error(`${f.name || f.to}: ${f.error}`));
      (d.skipped || []).forEach((f) => toast.info(`Skipped ${f.name}: ${f.reason}`));
      setPicked([]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setNotifying(false); }
  };

  const loadStatements = async (m) => {
    try {
      const { data } = await api.get("/consultants/statements", { params: { month: m } });
      setStatements(data || []);
    } catch (e) { setStatements([]); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadStatements(month); }, [month]);

  const addConsultant = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setBusy(true);
    try {
      await api.post("/consultants", form);
      toast.success(`${form.name} added`);
      setForm({ name: "", email: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Issue a one-time password. It comes back once and is never stored in the
  // clear, so if it is lost the only option is to generate another.
  const issuePassword = async (c) => {
    const ok = window.confirm(
      `Generate a new password for ${c.name}?\n\nIt is shown once. Any password they already have stops working.`);
    if (!ok) return;
    try {
      const { data } = await api.post(`/consultants/${c.id}/set-password`);
      setIssued(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "inactive" : "active";
    if (next === "inactive" &&
        !window.confirm(`Deactivate ${c.name}?\n\nTheir sign-in stops working immediately and they lose access to their statements.`)) return;
    try {
      await api.patch(`/consultants/${c.id}`, { status: next });
      toast.success(`${c.name} is now ${next}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upload = async (file, dry) => {
    if (!file) return;
    if (!month) { toast.error("Pick a month first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("month", month);
    fd.append("dry_run", dry ? "true" : "false");
    setBusy(true);
    try {
      const { data } = await api.post("/consultants/statements/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setPending(dry ? file : null);
      if (!dry) {
        toast.success(`${data.statements} statement(s) generated`);
        loadStatements(month);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Doctors</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Doctors paid per consult. Separate from employees — no attendance, leave or payroll.
        </p>
      </div>
        {mailOk ? (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" disabled={mailing === "bulk"}
                    onClick={bulkInvite} data-testid="con-mail-bulk">
              {mailing === "bulk" ? "Sending..." : "Email login to everyone pending"}
            </Button>
          </div>
        ) : null}

      <Tabs defaultValue="people" className="space-y-4">
        <TabsList>
          <TabsTrigger value="people">Doctors</TabsTrigger>
          <TabsTrigger value="upload">Monthly upload</TabsTrigger>
          <TabsTrigger value="statements">Statements</TabsTrigger>
        </TabsList>

        <TabsContent value="people" className="space-y-4">
          <Card className="border-zinc-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <UserPlus className="h-4 w-4 text-emerald-600" /> Add a consultant
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={addConsultant} className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs text-zinc-500">Name (must match the export exactly)</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Tarun Gupta" className="h-9" data-testid="consultant-name" />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs text-zinc-500">Google email they sign in with</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="name@gmail.com" className="h-9" data-testid="consultant-email" />
                </div>
                <Button type="submit" disabled={busy} className="h-9" data-testid="consultant-add">Add</Button>
              </form>
              <p className="mt-2 text-[11px] text-zinc-500">
                They fill their own bank and KYC details when they first sign in.
              </p>
            </CardContent>
          </Card>

          {issued && (
            <Card className="border-emerald-300 bg-emerald-50 shadow-none">
              <CardContent className="p-4">
                <div className="text-sm font-medium text-emerald-900">
                  Password for {issued.name} — shown once
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <code className="rounded bg-white border border-emerald-200 px-3 py-1.5 text-base font-mono tracking-wide"
                    data-testid="issued-password">{issued.password}</code>
                  <Button size="sm" variant="outline" className="h-8"
                    onClick={() => { navigator.clipboard?.writeText(issued.password); toast.success("Copied"); }}>
                    Copy
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setIssued(null)}>
                    Done
                  </Button>
                </div>
                <p className="mt-2 text-[11px] text-emerald-800">
                  Send this to {issued.email} along with the link
                  <span className="font-medium"> /consultant/login</span>. They must change it on first sign-in.
                  It cannot be retrieved later.
                </p>
              </CardContent>
            </Card>
          )}

          <Card className="border-zinc-200 shadow-none"><CardContent className="p-0">
            <FilterableTable
              kind="consultants"
              rows={rows}
              rowKey={(c) => c.id}
              emptyText="No consultants yet. Add one above."
              columns={[
                { key: "name", label: "Name",
                  render: (c) => (
                    <button className="text-left hover:underline font-medium"
                            onClick={() => setDetail(c)}
                            data-testid={`con-open-${c.id}`}>{c.name}</button>
                  ) },
                { key: "email", label: "Login email" },
                { key: "phone", label: "Phone" },
                { key: "profile_complete", label: "Bank details",
                  value: (c) => (c.profile_complete ? "Complete" : "Awaiting doctor"),
                  render: (c) => (
                    <Badge variant="outline" className={c.profile_complete
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"}>
                      {c.profile_complete ? "Complete" : "Awaiting doctor"}
                    </Badge>
                  ) },
                { key: "status", label: "Status",
                  render: (c) => (
                    <Badge variant="outline" className={c.status === "active"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-zinc-100 text-zinc-600"}>{c.status}</Badge>
                  ) },
                { key: "last_seen_at", label: "Last seen", type: "date",
                  value: (c) => since(c.last_seen_at),
                  render: (c) => (
                    <span className={c.last_seen_at ? "" : "text-amber-700"}>
                      {since(c.last_seen_at)}
                    </span>
                  ) },
                { key: "actions", label: "Actions", align: "right",
                  value: () => "",
                  render: (c) => (
                    <>
                      {mailOk && c.status === "active" && c.email ? (
                        <>
                          <button className="text-xs text-sky-700 hover:underline disabled:opacity-50"
                                  disabled={mailing === c.id}
                                  onClick={() => emailInvite(c)}
                                  data-testid={`con-mail-${c.id}`}>
                            {mailing === c.id ? "Sending..." : "Email login"}
                          </button>
                          <span className="text-zinc-300 mx-2">|</span>
                        </>
                      ) : null}
                      <button className="text-xs text-emerald-700 hover:underline"
                              onClick={() => issuePassword(c)}
                              data-testid={`con-pw-${c.id}`}>
                        {c.password_set_at ? "New password" : "Generate password"}
                      </button>
                      <span className="text-zinc-300 mx-2">|</span>
                      <button className="text-xs text-zinc-500 hover:underline"
                              onClick={() => toggleStatus(c)}
                              data-testid={`con-toggle-${c.id}`}>
                        {c.status === "active" ? "Deactivate" : "Reactivate"}
                      </button>
                    </>
                  ) },
              ]}
            />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="upload" className="space-y-4">
          <Card className="border-zinc-200 shadow-none"><CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Month</Label>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                  className="h-9 w-[170px]" data-testid="upload-month" />
              </div>
              <label className="ml-auto">
                <input type="file" className="hidden" disabled={busy}
                  accept=".xlsx,.xls"
                  onChange={(e) => { const f = e.target.files?.[0]; setPending(f || null); upload(f, true); e.target.value = ""; }}
                  data-testid="upload-preview-input" />
                <span className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-zinc-300 text-sm cursor-pointer hover:bg-zinc-50">
                  <Upload className="h-4 w-4" /> {busy ? "Reading…" : "Preview a file"}
                </span>
              </label>
            </div>
            <p className="mt-2 text-[11px] text-zinc-500">
              Preview first — it shows what each doctor would be paid and names anyone the
              file mentions who is not on your list. Nothing is saved until you confirm.
            </p>
          </CardContent></Card>

          {preview && (
            <Card className="border-zinc-200 shadow-none"><CardContent className="p-4 space-y-3">
              <div className="text-sm">
                <span className="font-medium">{preview.rows_read}</span> consults read
                {preview.period_label ? <> from <span className="font-medium">{preview.period_label}</span></> : null}
                {preview.rows_skipped ? <> · <span className="text-amber-700">{preview.rows_skipped} row(s) unreadable</span></> : null}
              </div>
              {!!(preview.unmatched || []).length && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="text-sm font-medium text-amber-900">
                    Not on your consultant list — these will be left out
                  </div>
                  <ul className="mt-1 text-sm text-amber-800">
                    {preview.unmatched.map((u) => (
                      <li key={u.doctor_name}>{u.doctor_name} — {u.consults} consult(s), {money(u.amount)}</li>
                    ))}
                  </ul>
                </div>
              )}
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Doctor</TableHead><TableHead>Tele</TableHead>
                  <TableHead>Video</TableHead><TableHead className="text-right">Payable</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(preview.matched || preview.saved || []).map((m) => (
                    <TableRow key={m.name}>
                      <TableCell>{m.name}</TableCell><TableCell>{m.tele}</TableCell>
                      <TableCell>{m.video}</TableCell>
                      <TableCell className="text-right">{money(m.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between border-t pt-3">
                <div className="text-sm font-medium">Total {money(preview.total_amount)}</div>
                {preview.dry_run && (
                <button
                  type="button"
                  disabled={busy || !pending}
                  onClick={() => upload(pending, false)}
                  data-testid="upload-confirm"
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50"
                >
                  {busy ? "Saving..." : "Save these statements"}
                </button>
              )}
              </div>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="statements" className="space-y-4">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="mr-2" data-testid="stmt-enet"
                      onClick={() => {
                        const d = new Date();
                        const def = `${String(d.getDate()).padStart(2, "0")}${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}`;
                        const pd = window.prompt("Payment date for the bank file (DD/MM/YYYY)", def);
                        if (pd) window.open(`/api/consultants/enet-export?month=${month}&payment_date=${pd}`, "_blank");
                      }}>
                Bank file for {month}
              </Button>
              <Button variant="outline" size="sm" data-testid="stmt-tracker"
                      onClick={() => window.open(`/api/consultants/statements/export?month=${month}`, "_blank")}>
                Download tracker for {month}
              </Button>
            </div>
            {mailOk ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-zinc-500">
                  {picked.length ? `${picked.length} selected` : "Select statements to notify"}
                </span>
                <Button variant="outline" size="sm" disabled={notifying || !picked.length}
                        onClick={notifySelected} data-testid="stmt-notify">
                  {notifying ? "Sending..." : "Email selected doctors"}
                </Button>
                <Button size="sm" disabled={notifying || !picked.length}
                        onClick={notifyPaid}
                        className="bg-emerald-600 hover:bg-emerald-700 ml-2"
                        data-testid="stmt-paid">
                  {notifying ? "Sending..." : "Payment credited"}
                </Button>
              </div>
            ) : null}
          <Card className="border-zinc-200 shadow-none"><CardContent className="p-4">
            <Label className="text-xs text-zinc-500">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="h-9 w-[170px]" />
          </CardContent></Card>
          <Card className="border-zinc-200 shadow-none"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead className="w-8"><input type="checkbox" aria-label="Select all" checked={statements.length > 0 && picked.length === statements.length} onChange={(e) => setPicked(e.target.checked ? statements.map((x) => x.id) : [])} /></TableHead><TableHead>Doctor</TableHead><TableHead>Tele</TableHead><TableHead>Video</TableHead>
                <TableHead className="text-right">Payable</TableHead>
                <TableHead className="text-right">Statement</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {statements.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="w-8">
                      <input type="checkbox"
                             aria-label={`Select ${s.doctor_name}`}
                             checked={picked.includes(s.id)}
                             onChange={() => togglePick(s.id)}
                             data-testid={`stmt-pick-${s.id}`} />
                    </TableCell>
                    <TableCell><input type="checkbox" aria-label={`Select ${s.doctor_name}`} checked={picked.includes(s.id)} onChange={() => togglePick(s.id)} /></TableCell><TableCell className="font-medium">{s.doctor_name}</TableCell>
                    <TableCell>{s.tele}</TableCell><TableCell>{s.video}</TableCell>
                    <TableCell className="text-right">{money(s.amount)}</TableCell>
                    <TableCell className="text-right">
                      <button className="text-xs text-emerald-700 hover:underline"
                        onClick={() => window.open(`/api/consultant/statements/${s.id}/download`, "_blank")}>
                        Download
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
                {!statements.length && (
                  <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-500 py-8">
                    Nothing generated for this month yet.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
        <ConsultantDetail c={detail} onClose={() => setDetail(null)}
                          onSaved={async (u) => { await load(); setDetail(u); }} />
    </div>
  );
}

// ---------------------------------------------------------------- detail
//
// Everything the doctor fills in themselves after signing in. The admin list
// endpoint already returns these fields - the page just never showed them,
// so all this does is put what is already on the wire onto the screen.

const DETAIL_SECTIONS = [
  ["Contact", [
    ["phone", "Phone"],
    ["personal_email", "Personal email"],
    ["dob", "Date of birth"],
    ["address", "Address"],
  ]],
  ["Emergency contact", [
    ["emergency_contact_name", "Name"],
    ["emergency_contact_phone", "Phone"],
  ]],
  ["Payment details", [
    ["account_holder_name", "Account holder"],
    ["bank_name", "Bank"],
    ["bank_account", "Account number"],
    ["bank_ifsc", "IFSC"],
    ["upi_id", "UPI ID"],
    ["pan", "PAN"],
  ]],
];

// Mirrors REQUIRED_PROFILE in backend/consultants.py. If that list changes,
// change this one too - they are kept in step by hand.
const REQUIRED_FIELDS = ["phone", "pan", "account_holder_name", "bank_name",
                         "bank_account", "bank_ifsc"];

const EDITABLE_FIELDS = DETAIL_SECTIONS.flatMap(([, f]) => f.map(([k]) => k));

const FIELD_LABELS = Object.fromEntries(
  DETAIL_SECTIONS.flatMap(([, fields]) => fields)
);

function DetailRow({ label, value }) {
  const filled = String(value == null ? "" : value).trim();
  return (
    <div className="flex justify-between gap-3 border-b border-zinc-100 py-1.5">
      <span className="text-[13px] text-zinc-500 shrink-0">{label}</span>
      <span className="text-[13px] text-zinc-900 text-right break-all">
        {filled || <span className="text-zinc-300">not filled in</span>}
      </span>
    </div>
  );
}

function fmtDay(v) {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString("en-IN",
    { day: "2-digit", month: "short", year: "numeric" });
}

function ConsultantDetail({ c, onClose, onSaved }) {
  // Hooks run on every render, so they sit above the early return.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [docStatements, setDocStatements] = useState([]);
  const [openMonth, setOpenMonth] = useState(null);
  const [cases, setCases] = useState([]);

  // The doctor's own months, shaped exactly as their portal shapes them.
  useEffect(() => {
    const id = c ? c.id : null;
    setOpenMonth(null);
    setCases([]);
    if (!id) { setDocStatements([]); return; }
    api.get(`/consultants/${id}/statements`)
      .then((r) => setDocStatements(r.data || []))
      .catch(() => setDocStatements([]));
  }, [c]);

  const openCases = async (st) => {
    if (openMonth === st.id) { setOpenMonth(null); setCases([]); return; }
    setOpenMonth(st.id);
    try {
      const { data } = await api.get(`/consultants/statements/${st.id}/cases`);
      setCases(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setCases([]);
    }
  };

  const startEdit = () => {
    const f = {};
    EDITABLE_FIELDS.forEach((k) => { f[k] = (c || {})[k] || ""; });
    setForm(f);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/consultants/${c.id}`, form);
      toast.success("Details updated");
      setEditing(false);
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const close = () => { setEditing(false); onClose(); };

  if (!c) return null;
  const missing = REQUIRED_FIELDS.filter((f) => !String(c[f] || "").trim());
  return (
    <Dialog open={!!c} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="con-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {c.name}
            <Badge variant="outline" className={c.status === "active"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-zinc-100 text-zinc-600"}>{c.status}</Badge>
            {editing ? null : (
              <button className="ml-auto text-xs font-normal text-emerald-700 hover:underline"
                      onClick={startEdit} data-testid="con-edit">
                Edit details
              </button>
            )}
          </DialogTitle>
        </DialogHeader>

          {/* What the doctor sees when they sign in. */}
          <div className="rounded-lg border border-zinc-200">
            <div className="px-3 py-2 border-b border-zinc-100 text-sm font-semibold">
              Monthly statements
              <span className="ml-2 text-[11px] font-normal text-zinc-500">
                as {c.name} sees them
              </span>
            </div>
            {docStatements.length === 0 ? (
              <div className="px-3 py-4 text-xs text-zinc-500">
                No statements yet. They appear once a month's consults are uploaded.
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {docStatements.map((st) => (
                  <div key={st.id || st.month}>
                    <button type="button" onClick={() => openCases(st)}
                            data-testid={`con-stmt-${st.month}`}
                            className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-zinc-50">
                      <span className="font-medium">{st.month}</span>
                      <span className="text-zinc-500 text-xs">
                        {st.total_consults != null ? `${st.total_consults} consults` : ""}
                      </span>
                      <span className="tabular-nums font-semibold text-emerald-700">
                        {"\u20b9 " + Number(st.net ?? st.payable ?? st.gross ?? 0).toLocaleString("en-IN")}
                      </span>
                    </button>
                    {openMonth === st.id && (
                      <div className="px-3 pb-3">
                        {cases.length === 0 ? (
                          <div className="text-xs text-zinc-500 py-2">No cases in this month.</div>
                        ) : (
                          <table className="w-full text-xs">
                            <tbody>
                              {cases.map((row, i) => (
                                <tr key={i} className="border-t border-zinc-100">
                                  {Object.values(row).slice(0, 5).map((v, j) => (
                                    <td key={j} className="py-1 pr-3 text-zinc-700">{String(v ?? "")}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        {editing && (
          <p className="text-[13px] text-zinc-500 -mt-1">
            Correcting something the doctor entered about themselves. They can
            still change it later from their own portal.
          </p>
        )}

        {missing.length > 0 ? (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[13px] text-amber-800">
            Still waiting on the doctor for: {missing.map((f) => FIELD_LABELS[f] || f).join(", ")}.
          </div>
        ) : (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[13px] text-emerald-800">
            The doctor has filled in everything needed to pay them.
          </div>
        )}

        <div className="space-y-5 pt-1">
          {DETAIL_SECTIONS.map(([title, fields]) => (
            <div key={title}>
              <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-medium mb-1">{title}</p>
              <div className="grid sm:grid-cols-2 gap-x-8">
                {fields.map(([key, label]) => (editing ? (
                  <div key={key} className="py-1">
                    <label className="text-[11px] text-zinc-500">{label}</label>
                    <input className="w-full border border-zinc-300 rounded px-2 py-1 text-[13px]"
                           value={form[key] || ""}
                           onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                           data-testid={`con-edit-${key}`} />
                  </div>
                ) : (
                  <DetailRow key={key} label={label} value={c[key]} />
                )))}
              </div>
            </div>
          ))}

          <div>
            <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-medium mb-1">Account</p>
            <div className="grid sm:grid-cols-2 gap-x-8">
              <DetailRow label="Login email" value={c.email} />
              <DetailRow label="Password"
                         value={c.password_set_at ? "Set by the doctor" : "Not set yet"} />
              <DetailRow label="Last signed in" value={since(c.last_seen_at)} />
              <DetailRow label="Added on" value={fmtDay(c.created_at)} />
              <DetailRow label="Added by" value={c.created_by} />
            </div>
          </div>

          {editing && (
            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
              <Button variant="outline" onClick={() => setEditing(false)}
                      disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="con-edit-save">
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
